import React, { Children } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { AnkhLogo } from '../components/AnkhLogo';
import { useLanguage } from '../components/LanguageContext';
import { translations } from '../translations';
import { TestimonialCarousel } from '../components/TestimonialCarousel';
import { InstagramIcon, LinkedinIcon, MailIcon } from 'lucide-react';
export function Home() {
  const {
    language
  } = useLanguage();
  const t = translations.home[language];
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: 'easeOut'
      }
    }
  };
  return <AnimatePresence mode="wait" data-id="element-125" data-mp="3ovatceysv16a">
      <motion.div key={language} initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.5
    }} className="w-full" data-id="element-126" data-mp="30wrpihk53zg2">
        {/* Hero Split Screen */}
        <div className="split-screen-container" data-id="element-127" data-mp="2qxu61t1kmw3x">
          {/* Left Image Side */}
          <div className="split-screen-left" data-id="element-128" data-mp="2mxa5efhshad1">
            <div className="absolute inset-0 bg-cover bg-center bg-no-repeat" style={{
            backgroundImage: 'url("https://cdn.magicpatterns.com/uploads/wz9NNYmZ46L9zRj8VVQn9s/2ef651e8ec5baac18e48393b78f32e61.jpg")'
          }} data-id="element-129" data-mp="2mwgvj5nja4qo" />
            <div className="absolute inset-0 bg-dark/40" data-id="element-130" data-mp="28840caw9spx8" />
          </div>

          {/* Right Content Side */}
          <div className="split-screen-right bg-dark !justify-start items-center justify-start" data-id="element-131" data-mp="239ath1bhnx61">
            <motion.div className="flex flex-col items-center text-center w-full max-w-md mt-[calc(35vh-150px)] md:mt-[calc(38vh-150px)]" variants={containerVariants} initial="hidden" animate="visible" data-id="element-132" data-mp="1z764wgzwdqq9">
              <motion.h1 variants={itemVariants} className="text-4xl md:text-5xl lg:text-6xl font-cinzel mb-12 tracking-wider leading-tight text-[#B89A6F] font-[300]" data-id="element-133" data-mp="1kk7gyw091ega">
                JULIETTE CAZABAT
              </motion.h1>

              <motion.div variants={itemVariants} className="space-y-4 mb-24 w-full" data-id="element-134" data-mp="1anunnz8m92m2">
                {t.titles.map((title, idx) => <p key={idx} className="text-rose font-inter text-xs md:text-sm uppercase tracking-widest" data-id="element-135" data-mp="1fm5ue13m6x02" data-mpi={idx}>
                    {title}
                  </p>)}
              </motion.div>

              <motion.div variants={itemVariants} className="mt-auto pt-12" data-id="element-136" data-mp="28906a9md8o1x">
                <AnkhLogo className="w-32 h-32" data-id="element-137" data-mp="1tm6xln7cqgip" />
              </motion.div>
            </motion.div>
          </div>
        </div>

        {/* Introduction Section */}
        <section className="bg-dark py-24 md:py-32 px-6" data-id="element-138" data-mp="2ma2ryl9g7wcb">
          <div className="max-w-3xl mx-auto text-center" data-id="element-139" data-mp="2m8hykyyvejg1">
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-100px'
          }} transition={{
            duration: 1
          }} data-id="element-140" data-mp="2r1b2jkmilnr2">
              <h2 className="text-3xl md:text-4xl font-cinzel text-gold mb-8 font-[300]" data-id="element-141" data-mp="odvysrfm92nc">
                {t.introTitle}
              </h2>
              <p className="text-cream/80 md:text-2xl mb-12 font-[Inter,_sans-serif] text-[20px]" style={{
              fontSize: "18px"
            }} data-id="element-142" data-mp="kdtusbmerx4w">
                {t.introText}
              </p>
              <Link to="/services" className="inline-block" data-id="element-143" data-mp="3nukgdyuh4irp">
                <button className="btn-outline-gold" data-id="element-144" data-mp="5o5u9cm1x2i8">
                  {'Book a free consultation'}
                </button>
              </Link>
            </motion.div>
          </div>
        </section>

        {/* Testimonials Carousel */}
        <TestimonialCarousel data-id="element-145" data-mp="1h18a25gbto1c" />

        {/* About Me Section */}
        <section className="bg-[#0D0D0D] py-24 md:py-32 px-6" data-id="element-146" data-mp="1c488a6qif7g1">
          <div className="max-w-6xl mx-auto" data-id="element-147" data-mp="1c62ppb2kohfd">
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-100px'
          }} transition={{
            duration: 1
          }} data-id="element-148" data-mp="27ey9hvtm7xy2">
              <div className="md:clearfix" data-id="element-149" data-mp="2mbfiwhi8v2hz">
                <h2 className="text-3xl md:text-4xl font-cinzel text-[#B89A6F] mb-10 tracking-wider" data-id="element-150" data-mp="2mcgsehjm3gxl">
                  {t.aboutTitle}
                </h2>

                {/* Portrait floats so text wraps naturally around it on desktop */}
                <div className="mb-8 md:mb-4 md:float-left md:mr-10 md:mt-2 w-full md:w-[42%] max-w-md" data-id="element-151" data-mp="3020stgpq023q">
                  <div className="aspect-[3/4] rounded-lg overflow-hidden bg-[#1a1a1a]" data-id="element-152" data-mp="3526r3fqhwmus">
                    <img src="https://cdn.magicpatterns.com/uploads/v5DWB986M4QVkGhPDFZFcm/1E148774-1811-44B2-979C-933419BA7043_1_105_c.jpg" alt="Juliette Cazabat portrait" className="w-full h-full object-cover rounded-lg" data-id="element-153" data-mp="3a19uywuqon16" />
                  </div>
                </div>

                <div className="text-cream/80 font-[Inter,_sans-serif] text-[18px] leading-[1.7]" data-id="element-154" data-mp="12cn8xao2nkcj">
                  <p className="mb-6" style={{
                  fontSize: "16px"
                }} data-id="element-155" data-mp="yfr81e0bvh8t">
                    Juliette is a French-born therapist and entrepreneur who has
                    called New Zealand home for more than ten years. With a
                    foundation in psychology and a longstanding interest in
                    holistic healing, her work integrates clinical understanding
                    with embodied and transformational approaches to wellbeing.
                  </p>
                  <p className="mb-8" style={{
                  fontSize: "16px"
                }} data-id="element-156" data-mp="1cbbh7hq98l31">
                    Her professional path has been deeply informed by
                    experiences of cultural transition, identity evolution and
                    motherhood — perspectives that continue to shape her
                    approach to therapy and human connection.
                  </p>

                  
                  <ul className="list-disc pl-6 mb-8 space-y-2 marker:text-[#B89A6F]" data-id="element-157" data-mp="3ubdwzn157zzb">
                    <li style={{
                    fontSize: "16px"
                  }} data-id="element-158" data-mp="26a15vaza5ko">BSc Psychology (University of Lyon)</li>
                    <li style={{
                    fontSize: "16px"
                  }} data-id="element-159" data-mp="3kjzst1y8ybzu">
                      Advanced studies in holistic healing and somatic
                      approaches
                    </li>
                    <li style={{
                    fontSize: "16px"
                  }} data-id="element-160" data-mp="3ambslwhvgcaa">
                      Ongoing professional development in relationship and
                      transformational work
                    </li>
                  </ul>

                  <p className="mb-2" style={{
                  fontSize: "16px"
                }} data-id="element-161" data-mp="31nhrptxiczqx">
                    Over the years, Juliette has supported individuals and
                    couples navigating emotional challenges, relationship
                    dynamics, life transitions, and personal growth. Her
                    approach combines psychological insight, somatic awareness,
                    and intuitive guidance, offering a grounded and
                    compassionate space for meaningful transformation.
                  </p>
                </div>

                <div className="md:clear-both" data-id="element-162" data-mp="1npn8s6c10pzj" />
              </div>

              {/* Nexus Dance Paragraph — flows below the grid */}
              <div className="mt-16 max-w-4xl mx-auto" data-id="element-163" data-mp="1iydjx7yaxqop">
                <p className="text-cream/80 md:text-xl text-[16px] font-[Inter,_sans-serif]" style={{
                fontSize: "16px"
              }} data-id="element-164" data-mp="1xnt7kf2jdg06">
                  {t.nexusParagraph}
                </p>

                {/* Explore More — Two Column */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-12 md:gap-16 mt-20" data-id="element-165" data-mp="26nq1av3s9523">
                  {/* Left: Nexus Dance */}
                  <div className="flex flex-col items-center text-center" data-id="element-166" data-mp="22nxna2vme6pc">
                    <div className="w-44 h-44 md:w-52 md:h-52 rounded-full overflow-hidden border-2 border-[#B89A6F]/50 shadow-lg mb-8" data-id="element-167" data-mp="cbi235kfi20i">
                      <img src="https://cdn.magicpatterns.com/uploads/3YyeuDth3JnNgJDPLQQDFB/WhatsApp_Image_2026-03-08_at_4.00.24_PM.jpg" alt="Nexus Dance School" className="w-full h-full object-cover" data-id="element-168" data-mp="8bhm3gholirc" />
                    </div>
                    <a href="https://nexusdance.wixsite.com/nexus" target="_blank" rel="noopener noreferrer" className="inline-block font-cinzel text-sm tracking-wider px-8 py-3 border border-[#B89A6F] text-[#B89A6F] hover:bg-[#B89A6F] hover:text-[#0D0D0D] transition-all duration-300 rounded-sm" data-id="element-169" data-mp="16jcwk737q6ux">
                      {t.visitNexus}
                    </a>
                  </div>

                  {/* Right: Retreats */}
                  <div className="flex flex-col items-center text-center" data-id="element-170" data-mp="muugcf06q3sd">
                    <div className="w-44 h-44 md:w-52 md:h-52 rounded-full overflow-hidden border-2 border-[#B89A6F]/50 shadow-lg mb-8" data-id="element-171" data-mp="rvi48rt4a4id">
                      <img src="https://cdn.magicpatterns.com/uploads/hEiBW9AQDHknXofxTEmk7s/0c1dd352a4a36fd9353471ca70e06926.jpg" alt="Kawau Island retreat landscape" className="w-full h-full object-cover" data-id="element-172" data-mp="1yaiz0u6eognd" />
                    </div>
                    <Link to="/retreats" className="inline-block font-cinzel text-sm tracking-wider px-8 py-3 border border-[#B89A6F] text-[#B89A6F] hover:bg-[#B89A6F] hover:text-[#0D0D0D] transition-all duration-300 rounded-sm" data-id="element-173" data-mp="28ezevnjvxxr6">
                      {t.explorePastRetreats}
                    </Link>
                  </div>
                </div>
              </div>

              {/* Professional Social Footer */}
              <div className="mt-20 pt-12 border-t border-cream/10 flex justify-center items-center gap-8" data-id="element-174" data-mp="1porf9ffe9pfq">
                <a href="https://www.instagram.com/therapy.nz/" target="_blank" rel="noopener noreferrer" className="text-cream/50 hover:text-[#B89A6F] transition-colors duration-300" aria-label="Juliette's professional Instagram" data-id="element-175" data-mp="1kovc1ieiu218">
                  <InstagramIcon className="w-5 h-5" data-id="element-176" data-mp="3f2w8xxqigeyo" />
                </a>
                <a href="https://www.linkedin.com/in/juliette-cazabat-b2504b3a3/" target="_blank" rel="noopener noreferrer" className="text-cream/50 hover:text-[#B89A6F] transition-colors duration-300" aria-label="Juliette's LinkedIn" data-id="element-177" data-mp="3b1ktgl1cpa47">
                  <LinkedinIcon className="w-5 h-5" data-id="element-178" data-mp="8mq9rp7c9g7b" />
                </a>
                <a href="mailto:juliettecazabat.nz@gmail.com" className="text-cream/50 hover:text-[#B89A6F] transition-colors duration-300" aria-label="Email Juliette" data-id="element-179" data-mp="mlptqjjwp6x5">
                  <MailIcon className="w-5 h-5" data-id="element-180" data-mp="3q3816pvrobx1" />
                </a>
              </div>
            </motion.div>
          </div>
        </section>
      </motion.div>
    </AnimatePresence>;
}